// hooks/useWorkflowEditor.ts
import { useState, useCallback, useEffect, useRef } from 'react';
import {
    Node,
    Edge,
    Connection,
    useNodesState,
    useEdgesState,
    useReactFlow,
    addEdge,
    NodeChange,
    XYPosition
} from 'reactflow';
import { v4 as uuidv4 } from 'uuid';
import { useManageConfiguration } from './useConfiguration';
import { showSuccessNotification, showErrorNotification } from '@/shared/utils/notify';
import { useNavigate } from 'react-router-dom';
import { createFlowFromConfig } from "@/modules/action-flow/shared/utils/workflowProcessor";
import { useGetActionFlowConfiguration } from '../../api';
import {createEdgesFromTransitions} from "@/modules/action-flow/config/types/edges";
import {CustomNode, formatNodeId, NodeData, ViewportState, WorkflowConfig} from "@/modules/action-flow/config";

export const useWorkflowEditor = (id?: string) => {
    const navigate = useNavigate();
    const [nodes, setNodes, onNodesChange] = useNodesState<CustomNode>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [selectedNode, setSelectedNode] = useState<CustomNode | null>(null);
    const [maxLevel, setMaxLevel] = useState<number>(99);
    const [isAddNodeModalOpen, setIsAddNodeModalOpen] = useState(false);
    const [newNodeType, setNewNodeType] = useState<string>('');
    const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);
    const [pastedConfig, setPastedConfig] = useState('');
    const [currentTransitions, setCurrentTransitions] = useState<any[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Local state for workflow metadata
    const [workflowName, setWorkflowName] = useState<string>('New Workflow');
    const [workflowDescription, setWorkflowDescription] = useState<string>('');
    const [workflowVersion, setWorkflowVersion] = useState<string>('1.0');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { getViewport, setViewport, project } = useReactFlow();
    const { validateConfiguration, createConfiguration, updateConfiguration } = useManageConfiguration();

    // Fetch configuration data using React Query
    const { data: configData, isLoading: isConfigLoading } = useGetActionFlowConfiguration(
        id || '',
        {
            query: {
                enabled: !!id && id !== 'new',
                // Don't auto-refetch when window regains focus
                refetchOnWindowFocus: false
            }
        }
    );

    // Load configuration when data is available
    useEffect(() => {
        if (configData && id && id !== 'new') {
            try {
                const config = configData;
                // @ts-ignore
                setWorkflowName(config.name);
                setWorkflowDescription(config.description || '');
                setWorkflowVersion(config.version || '1.0');

                // Parse the configuration JSON
                // @ts-ignore
                const parsedConfig = JSON.parse(config.configurationJson);

                // Set nodes
                if (parsedConfig.states) {
                    setNodes(parsedConfig.states);

                    // Calculate max level from nodes
                    const nodeMaxLevel = Math.max(
                        ...parsedConfig.states
                            // @ts-ignore
                            .filter(node =>
                                node.type !== 'approved' &&
                                node.type !== 'rejected'
                            )
                            // @ts-ignore
                            .map(node => node.data.level || 0),
                        0
                    );
                    setMaxLevel(nodeMaxLevel);
                }

                // Set viewport if available
                if (parsedConfig.viewportState) {
                    setViewport(parsedConfig.viewportState);
                }

                // Generate edges from transitions if available
                if (parsedConfig.transitions) {
                    const newEdges = createEdgesFromTransitions(
                        parsedConfig.transitions,
                        parsedConfig.states
                    );
                    setEdges(newEdges);
                }

                showSuccessNotification('Workflow configuration loaded successfully');
            } catch (error) {
                console.error('Error parsing configuration:', error);
                showErrorNotification('Failed to load workflow configuration: Invalid JSON');
            }
        }
    }, [configData, id, setNodes, setEdges, setViewport]);

    // Regenerate edges when nodes change
    const regenerateEdges = useCallback(() => {
        const config: WorkflowConfig = {
            workflowId: id || `workflow-${Date.now()}`,
            name: workflowName,
            description: workflowDescription,
            version: workflowVersion,
            // @ts-ignore
            states: nodes,
        };

        const processedConfig = createFlowFromConfig(config);
        if (processedConfig.transitions) {
            setCurrentTransitions(processedConfig.transitions);
            const newEdges = createEdgesFromTransitions(processedConfig.transitions, processedConfig.states);
            setEdges(newEdges);
        } else {
            setCurrentTransitions([]);
        }
    }, [nodes, workflowName, workflowDescription, workflowVersion, id, setEdges]);

    // Load configuration from JSON
    const loadConfigFromJson = useCallback((jsonConfig: string) => {
        try {
            const config: WorkflowConfig = JSON.parse(jsonConfig);
            setWorkflowName(config.name);
            setWorkflowDescription(config.description);
            setWorkflowVersion(config.version || '1.0');

            // Set nodes
            if (config.states) {
                // @ts-ignore
                setNodes(config.states);

                // Calculate max level from nodes
                const nodeMaxLevel = Math.max(
                    ...config.states
                        .filter(node =>
                            node.type !== 'approved' &&
                            node.type !== 'rejected'
                        )
                        .map(node => node.data.level || 0),
                    0
                );
                setMaxLevel(nodeMaxLevel);
            }

            // Set viewport if available
            if (config.viewportState) {
                setViewport(config.viewportState);
            }

            // Generate edges from transitions if available
            if (config.transitions) {
                const newEdges = createEdgesFromTransitions(config.transitions, config.states);
                setEdges(newEdges);
            } else {
                setEdges([]);
            }

            showSuccessNotification('Workflow configuration loaded successfully');
        } catch (error) {
            console.error('Error parsing configuration:', error);
            showErrorNotification('Failed to load workflow configuration: Invalid JSON');
        }
    }, [setNodes, setEdges, setViewport]);

    // Handle node connection
    const onConnect = useCallback(
        (params: Edge | Connection) => setEdges((eds) => addEdge(params, eds)),
        [setEdges]
    );

    // Handle node click
    const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
        setSelectedNode(node as CustomNode);
    }, []);

    // Handle nodes change with position tracking for selected node
    const onNodesChangeHandler = useCallback(
        (changes: NodeChange[]) => {
            onNodesChange(changes);
            changes.forEach((change) => {
                if (change.type === 'position' && selectedNode && change.id === selectedNode.id) {
                    setSelectedNode((prev) =>
                        prev ? {...prev, position: change.position as XYPosition} : null
                    );
                }
            });
        },
        [onNodesChange, selectedNode]
    );

    // Update node properties
    const updateNode = useCallback((id: string, data: Partial<NodeData>) => {
        setNodes((nds) => {
            const updatedNodes = nds.map((node) => {
                if (node.id === id) {
                    const updatedNode = {...node, data: {...node.data, ...data}};
                    if (data.label) {
                        updatedNode.id = formatNodeId(data.label);
                    }
                    
                    // Update selectedNode if it's the same node being updated
                    if (selectedNode && selectedNode.id === id) {
                        // @ts-ignore
                        setSelectedNode(updatedNode);
                    }
                    
                    return updatedNode;
                }
                return node;
            });

            // @ts-ignore
            const labels = updatedNodes.map(node => node.data.label);
            if (new Set(labels).size !== labels.length) {
                showErrorNotification('Duplicate node labels are not allowed');
                return nds;
            }

            setTimeout(() => regenerateEdges(), 0);
            return updatedNodes;
        });
    }, [setNodes, regenerateEdges, selectedNode]);

    // Add a new node
    const addNode = useCallback(
        (type: string) => {
            if (type === 'maker' && nodes.some(node => node.type === 'maker')) {
                showErrorNotification('Only one Initiator node is allowed');
                return;
            }

            if ((type === 'approved' || type === 'rejected') && nodes.some(node => node.type === type)) {
                showErrorNotification(`Only one ${type.charAt(0).toUpperCase() + type.slice(1)} node is allowed`);
                return;
            }

            const label = type === 'approved' ? 'APPROVED'
                : type === 'rejected' ? 'REJECTED'
                    : `${type.charAt(0).toUpperCase() + type.slice(1)} ${nodes.length + 1}`;

            let level = type === 'maker' ? 0
                : type === 'approved' || type === 'rejected' ? 100
                    : Math.min(maxLevel + 1, 99);

            const newNode: CustomNode = {
                id: formatNodeId(label),
                type,
                position: project({ x: 100, y: 100 }),
                data: {
                    label,
                    type,
                    description: getDefaultDescription(type),
                    level,
                    notificationConfigurations: [],
                    ...(type === 'validator' ? {
                        actions: [],
                        approvalConfig: {
                            minApprovals: 1,
                            totalApprovals: 1
                        }
                    } : {}),
                    ...(type === 'router' ? {
                        decisionRules: [],
                        defaultEvent: 'DEFAULT',
                        defaultNextNode: ''
                    } : {}),
                    ...(type === 'executor' ? {
                        beanName: '',
                        transitions: []
                    } : {})
                },
            };

            // @ts-ignore
            setNodes((nds) => {
                const newNodes = [...nds, newNode];
                setTimeout(() => regenerateEdges(), 0);
                return newNodes;
            });

            if (level > maxLevel && level < 100) {
                setMaxLevel(level);
            }

            setIsAddNodeModalOpen(false);
        },
        [project, setNodes, nodes, maxLevel, regenerateEdges]
    );

    // Get default description based on node type
    const getDefaultDescription = (type: string): string => {
        const descriptions: { [key: string]: string } = {
            maker: 'The starting point of the workflow, where data is input.',
            validator: 'A human-operated node for verifying, approving, rejecting, or returning requests for amendment.',
            router: 'A node with predefined rules to route requests based on specific criteria.',
            executor: 'A node that performs automated actions or executes Java code.',
            timer: 'Introduces a timed delay in the workflow.',
            alerter: 'Sends notifications or alerts to relevant parties.',
            collector: 'Combines data from multiple branches of the workflow.',
            approved: 'Marks the end of a workflow on approved path.',
            rejected: 'Marks the end of a workflow on rejected path.',
        };
        return descriptions[type] || '';
    };

    // Remove a node
    const removeNode = useCallback((id: string) => {
        setNodes((nds) => nds.filter((node) => node.id !== id));
        setEdges((eds) => eds.filter((edge) => edge.source !== id && edge.target !== id));

        if (selectedNode && selectedNode.id === id) {
            setSelectedNode(null);
        }

        setTimeout(() => regenerateEdges(), 0);
    }, [setNodes, setEdges, selectedNode, regenerateEdges]);

    // Move a node in the nodes array
    const moveNode = useCallback((index: number, direction: 'up' | 'down') => {
        setNodes((currentNodes) => {
            const newNodes = [...currentNodes];
            const newIndex = direction === 'up' ? index - 1 : index + 1;

            if (newIndex >= 0 && newIndex < newNodes.length) {
                [newNodes[index], newNodes[newIndex]] = [newNodes[newIndex], newNodes[index]];
            }

            setTimeout(() => regenerateEdges(), 0);
            return newNodes;
        });
    }, [setNodes, regenerateEdges]);

    // Handle file upload
    const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target?.result as string;
                loadConfigFromJson(content);
            };
            reader.readAsText(file);
        }
    }, [loadConfigFromJson]);

    // Handle pasted config
    const handlePasteConfig = useCallback(() => {
        loadConfigFromJson(pastedConfig);
        setIsLoadModalOpen(false);
        setPastedConfig('');
    }, [pastedConfig, loadConfigFromJson]);

    // Save workflow configuration
    const saveWorkflow = useCallback(async () => {
        if (!workflowName) {
            showErrorNotification('Please provide a workflow name');
            return;
        }

        setIsLoading(true);

        try {
            const viewportState: ViewportState = getViewport();

            const config: WorkflowConfig = {
                workflowId: id || `workflow-${uuidv4()}`,
                name: workflowName,
                description: workflowDescription,
                version: workflowVersion,
                // @ts-ignore
                states: nodes,
                viewportState,
            };

            if (!validateConfiguration(config)) {
                setIsLoading(false);
                return;
            }

            const processedConfig = createFlowFromConfig(config);
            const configJson = JSON.stringify(processedConfig);

            const dto = {
                id: id !== 'new' ? id : undefined,
                workflowId: config.workflowId,
                name: config.name,
                description: config.description,
                version: config.version,
                configurationJson: configJson,
            };

            if (id && id !== 'new') {
                // @ts-ignore
                const result = await updateConfiguration(id, dto);
                // Success notification is handled in useConfiguration hook
                // Navigate to the configuration list or to the new version if created
                navigate('/admin/action-flow/configurations');
            } else {
                // @ts-ignore
                await createConfiguration(dto);
                showSuccessNotification('Workflow configuration created successfully');
                navigate('/admin/action-flow/configurations');
            }
        } catch (error) {
            showErrorNotification(`Error saving workflow: ${error}`);
        } finally {
            setIsLoading(false);
        }
    }, [
        workflowName,
        workflowDescription,
        workflowVersion,
        nodes,
        id,
        getViewport,
        validateConfiguration,
        createConfiguration,
        updateConfiguration,
        navigate
    ]);

    return {
        // State
        nodes,
        edges,
        selectedNode,
        maxLevel,
        isAddNodeModalOpen,
        newNodeType,
        isLoadModalOpen,
        pastedConfig,
        currentTransitions,
        fileInputRef,
        workflowName,
        workflowDescription,
        workflowVersion,
        isLoading: isLoading || isConfigLoading,

        // Setters
        setSelectedNode,
        setIsAddNodeModalOpen,
        setNewNodeType,
        setIsLoadModalOpen,
        setPastedConfig,
        setWorkflowName,
        setWorkflowDescription,
        setWorkflowVersion,

        // Node operations
        onNodesChangeHandler,
        onEdgesChange,
        onConnect,
        onNodeClick,
        updateNode,
        addNode,
        removeNode,
        moveNode,

        // File operations
        handleFileUpload,
        handlePasteConfig,
        loadConfigFromJson,

        // Save operation
        saveWorkflow,

        // Utility functions
        regenerateEdges,
        getDefaultDescription
    };
};

export default useWorkflowEditor;